/**
 * Enhanced audio utilities combining Flowcus robustness with Google's efficiency
 * @license Apache-2.0
 */

import { Blob } from '@google/genai';

/**
 * Encode Uint8Array to base64 string
 */
function encode(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Decode base64 string to Uint8Array
 */
function decode(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Create a Gemini-compatible blob from Float32Array audio data
 * Converts -1 to 1 float range to int16 PCM format
 */
function createBlob(data: Float32Array): Blob {
  const l = data.length;
  const int16 = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    // Convert float32 -1 to 1 to int16 -32768 to 32767
    int16[i] = Math.max(-32768, Math.min(32767, data[i] * 32768));
  }

  return {
    data: encode(new Uint8Array(int16.buffer)),
    mimeType: 'audio/pcm;rate=16000',
  };
}

/**
 * Decode audio data from Gemini response into AudioBuffer
 * More robust than Google's version with better error handling
 */
async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  try {
    // Ensure we have valid parameters
    if (!data || data.length === 0) {
      throw new Error('Invalid audio data: empty or undefined');
    }
    
    if (numChannels < 1 || numChannels > 2) {
      throw new Error('Invalid channel count: must be 1 or 2');
    }

    const samplesPerChannel = data.length / 2 / numChannels;
    if (samplesPerChannel <= 0 || samplesPerChannel % 1 !== 0) {
      throw new Error('Invalid audio data length for specified channels');
    }

    const buffer = ctx.createBuffer(
      numChannels,
      samplesPerChannel,
      sampleRate,
    );

    const dataInt16 = new Int16Array(data.buffer);
    const l = dataInt16.length;
    const dataFloat32 = new Float32Array(l);
    
    // Convert int16 to float32 with proper bounds checking
    for (let i = 0; i < l; i++) {
      dataFloat32[i] = Math.max(-1, Math.min(1, dataInt16[i] / 32768.0));
    }

    // Handle mono vs stereo
    if (numChannels === 1) {
      buffer.copyToChannel(dataFloat32, 0);
    } else {
      // Extract interleaved channels
      for (let channel = 0; channel < numChannels; channel++) {
        const channelData = dataFloat32.filter(
          (_, index) => index % numChannels === channel,
        );
        buffer.copyToChannel(channelData, channel);
      }
    }

    return buffer;
  } catch (error) {
    console.error('Error decoding audio data:', error);
    // Return a silent buffer as fallback
    const buffer = ctx.createBuffer(numChannels, 1, sampleRate);
    for (let i = 0; i < numChannels; i++) {
      const channelData = buffer.getChannelData(i);
      channelData[0] = 0;
    }
    return buffer;
  }
}

/**
 * Enhanced audio context creation with better error handling
 */
export async function createAudioContexts(): Promise<{
  inputContext: AudioContext;
  outputContext: AudioContext;
}> {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    
    const inputContext = new AudioContextClass({
      sampleRate: 16000
    });
    
    const outputContext = new AudioContextClass({
      sampleRate: 24000
    });

    // Ensure contexts are in running state
    if (inputContext.state === 'suspended') {
      await inputContext.resume();
    }
    if (outputContext.state === 'suspended') {
      await outputContext.resume();
    }

    return { inputContext, outputContext };
  } catch (error) {
    console.error('Error creating audio contexts:', error);
    throw new Error('Failed to create audio contexts. Audio may not be supported.');
  }
}

/**
 * Create gain nodes with proper initialization
 */
export function createGainNodes(
  inputContext: AudioContext, 
  outputContext: AudioContext
): {
  inputNode: GainNode;
  outputNode: GainNode;
} {
  const inputNode = inputContext.createGain();
  const outputNode = outputContext.createGain();
  
  // Connect output to destination
  outputNode.connect(outputContext.destination);
  
  return { inputNode, outputNode };
}

export { encode, decode, createBlob, decodeAudioData };